import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Select,
  Stack,
  Text,
  Alert,
  Loader,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useGenerateEvaluationReport } from '../hooks/useEvaluationReports';
import { userApi } from '../../../api/users';

interface GenerateEvaluationReportModalProps {
  opened: boolean;
  onClose: () => void;
  tenderId: string;
  onSuccess?: () => void;
}

export function GenerateEvaluationReportModal({
  opened,
  onClose,
  tenderId,
  onSuccess,
}: GenerateEvaluationReportModalProps) {
  const [checkedById, setCheckedById] = useState<string | null>(null);
  const [approvedById, setApprovedById] = useState<string | null>(null);
  const [procurementOfficers, setProcurementOfficers] = useState<Array<{ value: string; label: string }>>([]);
  const [headOfProcurement, setHeadOfProcurement] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const generateReport = useGenerateEvaluationReport();

  useEffect(() => {
    if (opened) {
      loadUsers();
    }
  }, [opened]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const users = await userApi.getUsersForEvaluationReport();
      const officers = users
        .filter((u) => u.role === 'PROCUREMENT_OFFICER')
        .map((u) => ({ value: u.id, label: u.fullName }));
      const head = users
        .filter((u) => u.role === 'HEAD_OF_PROCUREMENT')
        .map((u) => ({ value: u.id, label: u.fullName }));

      setProcurementOfficers(officers);
      setHeadOfProcurement(head);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleGenerate = () => {
    if (!checkedById || !approvedById) {
      return;
    }

    generateReport.mutate(
      {
        tenderId,
        data: {
          checkedById,
          approvedById,
        },
      },
      {
        onSuccess: () => {
          onClose();
          setCheckedById(null);
          setApprovedById(null);
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Generate Evaluation Report"
      size="md"
    >
      <Stack gap="md">
        {loadingUsers ? (
          <Loader size="md" />
        ) : (
          <>
            {procurementOfficers.length === 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                No procurement officers found. Please add users with PROCUREMENT_OFFICER role.
              </Alert>
            )}

            {headOfProcurement.length === 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                No head of procurement found. Please add a user with HEAD_OF_PROCUREMENT role.
              </Alert>
            )}

            <Select
              label="Checked By (Procurement Officer)"
              placeholder="Select procurement officer"
              data={procurementOfficers}
              value={checkedById}
              onChange={(value) => setCheckedById(value)}
              required
              searchable
            />

            <Select
              label="Approved By (Head of Procurement)"
              placeholder="Select head of procurement"
              data={headOfProcurement}
              value={approvedById}
              onChange={(value) => setApprovedById(value)}
              required
              searchable
            />

            <Text size="sm" c="dimmed">
              Once generated, the report will be sent to the selected procurement officer for checking,
              then to the head of procurement for approval.
            </Text>

            <Button
              onClick={handleGenerate}
              loading={generateReport.isPending}
              disabled={!checkedById || !approvedById || procurementOfficers.length === 0 || headOfProcurement.length === 0}
              fullWidth
            >
              Generate Report
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}

